import {
    api,
    apiErrorMessage,
    groupThousands,
    ListBody,
    listSpec,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    Field,
    FileUpload,
    type ListColumnDef,
    PageHeader,
    SearchableSelect,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { BinImport } from '@/api/types';
import { P } from '@/lib/permissions';
import { useList, useWrite } from '@/lib/queries';

const PATH = '/staff/bin-ranges/imports';
const MAX_BYTES = 50 * 1024 * 1024;

const SOURCES = [
    { value: 'third_party', label: 'Third-party BIN list' },
    { value: 'acquirer', label: 'Acquirer feed' },
];

/** The reads of `/pricing/bin-imports` (BM-46 §2.0; the endpoint declares no list contract). */
export const binImportsReads = () => ({ list: listSpec(PATH) });

const columns: ListColumnDef<BinImport>[] = [
    {
        key: 'batch_id',
        label: 'Batch',
        locked: 'start',
        cell: (b) => <span className="font-mono text-xs">{b.batch_id}</span>,
        noTruncate: true,
    },
    {
        key: 'source',
        label: 'Source',
        cell: (b) =>
            SOURCES.find((s) => s.value === b.source)?.label ?? b.source,
    },
    {
        key: 'rows',
        label: 'Ranges',
        cell: (b) => groupThousands(String(b.rows)),
        noTruncate: true,
    },
    {
        key: 'imported_at',
        label: 'Imported',
        kind: 'datetime',
        cell: (b) => <DateTime value={b.imported_at} empty="—" />,
        noTruncate: true,
    },
];

/**
 * BM-62 decision 9: the BIN ranges that name a card's funding, issuer country, usage and tier at capture. An upload
 * replaces every range of its source in one transaction; a sale keeps what it was priced with.
 */
export function BinImportsPage() {
    const can = useAuthStore((s) => s.can);
    const query = useList<BinImport>(PATH);
    const [source, setSource] = useState<string | null>('third_party');
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const upload = useWrite(async () => {
        const body = new FormData();
        body.append('source', source ?? 'third_party');
        if (file) body.append('file', file);
        return (await api().post<BinImport>(PATH, body)).data;
    }, [PATH]);
    return (
        <div className="space-y-4">
            <PageHeader
                title="BIN ranges"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'Card attributes by BIN range, used to price each sale at capture. The acquirer feed wins over the third-party list where both cover a BIN.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            {can(P.binImport) ? (
                <Card data-testid="bin-upload">
                    <CardHeader>
                        <CardTitle className="text-sm font-medium">
                            Replace a source
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {error ? (
                            <p
                                role="alert"
                                className="text-destructive text-sm"
                            >
                                {error}
                            </p>
                        ) : null}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Field id="bin-source" label="Source">
                                <SearchableSelect
                                    id="bin-source"
                                    label="Source"
                                    value={source}
                                    options={SOURCES}
                                    onChange={setSource}
                                />
                            </Field>
                            <FileUpload
                                label="CSV file"
                                accept={['text/csv']}
                                maxBytes={MAX_BYTES}
                                value={file}
                                onChange={(f) => {
                                    setFile(f);
                                    setError(null);
                                }}
                                disabled={upload.isPending}
                            />
                        </div>
                        <p className="text-fg-2 text-xs">
                            Header:
                            bin_start,bin_end,brand,funding,issuer_country,card_usage,product_tier
                        </p>
                        <Button
                            size="sm"
                            disabled={!file || upload.isPending}
                            onClick={() =>
                                upload.mutate(undefined, {
                                    onSuccess: () => setFile(null),
                                    onError: (e) =>
                                        setError(apiErrorMessage(e)),
                                })
                            }
                        >
                            Import
                        </Button>
                    </CardContent>
                </Card>
            ) : null}
            <ListBody
                query={query}
                columns={columns}
                rowKey={(b) => b.batch_id}
            />
        </div>
    );
}
